'use client';

import { createTheme } from '@mui/material';
import { Inter_Tight } from 'next/font/google';
import colorsHex from '../palette/colors';
import { fontSizeVars, lineHeightVars, spacingVars } from '../themeVars.css';
import './muiOverrides.d.ts';
import { ExtendedTypographyOptions } from './muiThemeExtensions';

const interTight = Inter_Tight({ subsets: ['latin'], weight: ['300', '400', '500', '600'] });

export const fontWeightLight = 300;
export const fontWeightRegular = 400;
export const fontWeightDemiBold = 500;
export const fontWeightSemiBold = 600;

export const letterSpacingDefault = 0;

// fontSizeVars
const typography: ExtendedTypographyOptions = {
  poster: {
    fontSize: fontSizeVars.poster, // pxToRem(400),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.poster,
    ...interTight.style,
  },
  h1: {
    fontSize: fontSizeVars.h1, // pxToRem(152),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.h1,
    ...interTight.style,
  },
  h2: {
    fontSize: fontSizeVars.h2, // pxToRem(96),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.h2,
    ...interTight.style,
  },
  h3: {
    fontSize: fontSizeVars.h3, // pxToRem(80),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.h3,
    ...interTight.style,
  },
  h4: {
    fontSize: fontSizeVars.h4, // pxToRem(64),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.h4,
    ...interTight.style,
  },
  h5: {
    fontSize: fontSizeVars.h5, // pxToRem(40),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.h5,
    ...interTight.style,
  },
  h6: {
    fontSize: fontSizeVars.h6, // pxToRem(24),
    fontWeight: fontWeightSemiBold,
    lineHeight: lineHeightVars.h6,
    ...interTight.style,
  },
  body: {
    fontSize: fontSizeVars.body, // pxToRem(28),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.body,
    ...interTight.style,
  },
  large: {
    fontSize: fontSizeVars.large, // pxToRem(40),
    fontWeight: fontWeightLight,
    lineHeight: lineHeightVars.large,
    ...interTight.style,
  },
  small: {
    fontSize: fontSizeVars.small, // pxToRem(24),
    fontWeight: fontWeightRegular,
    lineHeight: lineHeightVars.small,
    ...interTight.style,
  },
  tiny: {
    fontSize: fontSizeVars.tiny, // pxToRem(16),
    fontWeight: fontWeightRegular,
    lineHeight: lineHeightVars.tiny,
    ...interTight.style,
  },
  button: {
    fontSize: fontSizeVars.button, // pxToRem(24),
    fontWeight: fontWeightSemiBold,
    lineHeight: lineHeightVars.button,
    ...interTight.style,
  },
  menuLink: {
    fontSize: fontSizeVars.menuLink, // pxToRem(24),
    fontWeight: fontWeightRegular,
    lineHeight: lineHeightVars.menuLink,
    ...interTight.style,
  },
  menuNumber: {
    fontSize: fontSizeVars.menuNumber, // pxToRem(24),
    fontWeight: fontWeightSemiBold,
    lineHeight: lineHeightVars.menuNumber,
    ...interTight.style,
  },
};

const theme = createTheme({
  typography,
  palette: {
    primary: {
      main: colorsHex.midnight,
      light: colorsHex.vibrantBlue200,
      dark: colorsHex.aquaDark,
    },
    secondary: {
      main: colorsHex.vibrantBlue200,
      light: colorsHex.vibrantBlue300,
      dark: colorsHex.vibrantBlue100,
    },
    grey: {
      100: colorsHex.sand100,
      200: colorsHex.sand200,
      300: colorsHex.sand300,
    },

    text: {
      primary: colorsHex.midnight,
      secondary: colorsHex.white,
    },
    background: {
      default: colorsHex.midnight,
    },
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      variants: [
        {
          props: { variant: 'unstyled' },
          style: {
            'fontWeight': 'normal',
            'textDecoration': 'underline',
            'padding': 'inherit',
            'color': colorsHex.midnight,
            'backgroundColor': 'transparent',
            'border': 'none',
            '&:hover': {
              color: colorsHex.midnight,
              backgroundColor: 'transparent',
              borderColor: 'none',
            },
          },
        },
        {
          props: { variant: 'text' },
          style: {
            'color': colorsHex.midnight,
            'textTransform': 'none',
            '&:hover': {
              backgroundColor: colorsHex.aquaDark,
              color: colorsHex.warmGrey100,
            },
          },
        },
        {
          props: { variant: 'outlinedPale' },
          style: {
            'color': colorsHex.white,
            'backgroundColor': 'transparent',
            'border': `1px solid ${colorsHex.white}`,
            'opacity': 0.6,
            'transition': 'opacity 0.3s',
            '&:hover': {
              opacity: 1,
              color: colorsHex.white,
              backgroundColor: 'transparent',
              borderColor: colorsHex.white,
            },
          },
        },
        {
          props: { variant: 'outlined' },
          style: {
            'color': colorsHex.midnight,
            'borderColor': colorsHex.midnight,
            '&:hover': {
              backgroundColor: colorsHex.yellowAcenda,
              borderColor: colorsHex.yellowAcenda,
            },
          },
        },
        {
          props: { variant: 'light' },
          style: {
            'padding': '16px 24px',
            'color': colorsHex.midnight,
            'backgroundColor': colorsHex.sand100,
            'border': `1px solid ${colorsHex.sand100}`,
            'transition': 'border-color 0.3s, color 0.3s background-color 0.3s',
            '&:hover': {
              opacity: 1,
              color: colorsHex.midnight,
              backgroundColor: colorsHex.yellowAcenda,
              borderColor: colorsHex.yellowAcenda,
            },
          },
        },
        {
          props: { variant: 'dark' },
          style: {
            'color': colorsHex.white,
            'backgroundColor': colorsHex.vibrantBlue200,
            'textTransform': 'none',
            '&:hover': {
              backgroundColor: colorsHex.midnight,
              color: colorsHex.white,
            },
          },
        },
      ],
      styleOverrides: {
        root: ({ ownerState }) => {
          const { size = 'medium' } = ownerState;

          return {
            'lineHeight': 1,

            ...typography.button,
            ...(size === 'small' && {
              padding: [spacingVars['12'], spacingVars['24']].join(' '),
              fontSize: fontSizeVars.button,
            }),
            ...(size === 'medium' && {
              padding: [spacingVars['16'], spacingVars['32']].join(' '),
            }),
            'color': colorsHex.midnight,
            'backgroundColor': colorsHex.gold,
            'borderColor': colorsHex.gold,
            '&:hover': {
              backgroundColor: colorsHex.yellowAcenda,
              borderColor: colorsHex.yellowAcenda,
              color: colorsHex.midnight,
            },
            'textTransform': 'none',
            'borderRadius': 999,
          };
        },
        contained: {
          '&:hover': {
            color: colorsHex.midnight,
          },
        },
        outlined: {
          'backgroundColor': 'transparent',
          '&:hover': {
            backgroundColor: colorsHex.aquaDark,
            borderColor: colorsHex.aquaDark,
            color: colorsHex.midnight,
          },
        },
        startIcon: {
          marginRight: spacingVars['8'],
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        maxWidth: 'lg',
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          'textTransform': 'none',
          'color': colorsHex.midnight,
          'backgroundColor': colorsHex.gold,
          '&.Mui-selected': {
            color: colorsHex.midnight,
            backgroundColor: colorsHex.aquaDark,
          },
        }),
      },
    },
    MuiTypography: {
      defaultProps: {
        variant: 'body',
        letterSpacing: letterSpacingDefault,
      },
    },
    MuiSvgIcon: {
      defaultProps: {
        fontSize: 'inherit',
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: () => {
          return {
            input: { fontFamily: interTight.style.fontFamily },
            fieldset: { borderRadius: 0 },
          };
        },
      },
    },
  },
});

export default theme;

export { typography as muiThemeTypography };
